// Verständigung zwischen mehreren Fenstern derselben Anwendung über den
// gerade aktiven Nutzer — QA-Befund 88.
//
// Die Sitzung ist geräteweit, nicht fensterweit: der Server entscheidet
// anhand des `active_sid`-Cookies, wer gerade arbeitet. Wechselt jemand
// in Fenster A den Nutzer, gilt das sofort auch für Fenster B — nur wusste
// B davon nichts und zeigte weiter den alten Namen im Kopf. Wer daraufhin
// in B etwas änderte, sah eine andere Person, als der Server einsetzte.
//
// Zwei Wege, weil einer allein nicht überall trägt:
//   BroadcastChannel  — der direkte, überall dort wo es ihn gibt
//   storage-Ereignis  — Rückfall für ältere iPad-Safaris
// Beide zusammen können dieselbe Nachricht zweimal zustellen; die
// Empfängerseite vergleicht deshalb mit ihrem eigenen Stand und tut
// nichts, wenn er schon passt.

const KANAL_NAME = 'tracking-engel-sitzung';
const SPEICHER_SCHLUESSEL = 'tracking-engel-sitzung-aktiv';

type Meldung = { aktiv: string | null; t: number };

let kanal: BroadcastChannel | null = null;

function holKanal(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  kanal ??= new BroadcastChannel(KANAL_NAME);
  return kanal;
}

function istMeldung(v: unknown): v is Meldung {
  if (typeof v !== 'object' || v === null) return false;
  const m = v as Partial<Meldung>;
  return m.aktiv === null || typeof m.aktiv === 'string';
}

/** Sagt allen anderen Fenstern, wer hier gerade angemeldet ist. */
export function meldeAktivenNutzer(aktiv: string | null): void {
  const meldung: Meldung = { aktiv, t: Date.now() };
  holKanal()?.postMessage(meldung);
  try {
    // Der Zeitstempel sorgt dafür, dass auch zweimal derselbe Nutzer
    // ein storage-Ereignis auslöst — der Browser meldet nur echte
    // Wertänderungen. Kein Geheimnis, nur ein Name: das steht in der
    // Oberfläche ohnehin und ist kein Ersatz für die Sitzung.
    localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(meldung));
  } catch {
    // Privater Modus ohne Speicher: dann trägt eben nur der Kanal.
  }
}

/** Hört auf Meldungen aus anderen Fenstern. Rückgabe hängt wieder ab. */
export function beobachteSitzung(fn: (aktiv: string | null) => void): () => void {
  const k = holKanal();

  const beiNachricht = (e: MessageEvent): void => {
    if (istMeldung(e.data)) fn(e.data.aktiv);
  };
  k?.addEventListener('message', beiNachricht);

  const beiSpeicher = (e: StorageEvent): void => {
    if (e.key !== SPEICHER_SCHLUESSEL || e.newValue === null) return;
    try {
      const daten: unknown = JSON.parse(e.newValue);
      if (istMeldung(daten)) fn(daten.aktiv);
    } catch {
      // Unlesbarer Eintrag: ignorieren, nicht abstürzen.
    }
  };
  window.addEventListener('storage', beiSpeicher);

  return () => {
    k?.removeEventListener('message', beiNachricht);
    window.removeEventListener('storage', beiSpeicher);
  };
}
